import axios from "axios";
import { useQuery } from "@tanstack/react-query";

interface IExchangeSymbolFilter {
  filterType: string;
  maxPrice: string;
  minPrice: string;
  tickSize: string;
}

export interface IExchangeSymbol {
  symbol: string;
  status: string;
  baseAsset: string;
  baseAssetPrecision: number;
  quoteAsset: string;
  quoteAssetPrecision: number;
  filters: IExchangeSymbolFilter[];
}

export interface IExchangeInfo {
  symbols: IExchangeSymbol[];
}

const baseUrl = "https://fapi.binance.com";
const STORAGE_KEY = "binanceSymbols";

export function useBinanceSymbols() {
  return useQuery<IExchangeSymbol[]>({
    queryKey: ["exchangeInfo"],
    queryFn: async () => {
      try {
        // Check sessionStorage first
        const cachedData = sessionStorage.getItem(STORAGE_KEY);
        if (cachedData) {
          console.log("Using cached symbols");
          try {
            const parsed = JSON.parse(cachedData);
            if (
              Array.isArray(parsed) &&
              parsed.length > 0 &&
              parsed[0].symbol
            ) {
              return parsed as IExchangeSymbol[];
            }
          } catch (e) {
            console.warn("Failed to parse cached symbols, fetching fresh data");
            sessionStorage.removeItem(STORAGE_KEY);
          }
        }

        const response = await axios.get<IExchangeInfo>(
          `${baseUrl}/fapi/v1/exchangeInfo`,
          {
            timeout: 10000, // 10 second timeout
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.data || !Array.isArray(response.data.symbols)) {
          console.error("Invalid response format:", response.data);
          throw new Error("Invalid response format from Binance API");
        }

        const filteredSymbols = response.data.symbols.filter(
          (symbol: IExchangeSymbol) =>
            symbol.status === "TRADING" &&
            symbol.symbol.includes("USDT") &&
            !symbol.symbol.includes("_"),
        );

        if (filteredSymbols.length === 0) {
          console.warn("No symbols matched the filter criteria");
        } else {
          // Cache the filtered symbols
          try {
            sessionStorage.setItem(
              STORAGE_KEY,
              JSON.stringify(filteredSymbols),
            );
          } catch (e) {
            console.warn("Failed to cache symbols in sessionStorage:", e);
          }
        }

        return filteredSymbols;
      } catch (error: any) {
        console.error("Failed to fetch exchange info:", {
          error: error?.message,
          status: error?.response?.status,
          data: error?.response?.data,
          config: {
            url: error?.config?.url,
            timeout: error?.config?.timeout,
            headers: error?.config?.headers,
          },
        });
        throw error;
      }
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
  });
}
